import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "@xyflow/react/dist/style.css";
import { privateRouter } from "./router.tsx";
import { RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider, theme } from "antd";
import { NotificationProvider } from "./components/util-components/Notifications/NotificationsProvider.tsx";
import { EnvironmentHostName, getEnvironment } from "./util/getEnvironment.ts";
import { unreachable } from "./util/unreachable.ts";
import { Helmet, HelmetProvider } from "react-helmet-async";
import * as amplitude from "@amplitude/analytics-browser";
import * as CookieConsent from "vanilla-cookieconsent";
import { getCookieConsentConfig } from "./components/CookieConsent/config.ts";
import { CookieValue } from "vanilla-cookieconsent";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const colorSchemes: Record<
  keyof typeof EnvironmentHostName,
  { colorPrimary: string; colorInfo: string }
> = {
  PRD: { colorPrimary: "#1e8c6f", colorInfo: "#1e8c6f" },
  STG: { colorPrimary: "#f59e42", colorInfo: "#f59e42" },
  SBX: { colorPrimary: "#3b82f6", colorInfo: "#3b82f6" },
  LOCAL: { colorPrimary: "#a855f7", colorInfo: "#a855f7" },
};

const helmetConfig = (() => {
  const env = getEnvironment();
  switch (env) {
    case "PRD":
      return { title: "Pleevi", favicon: "/logo.svg" };
    case "STG":
      return { title: "[STG] Pleevi", favicon: "/logo-stg.svg" };
    case "SBX":
      return { title: "[SBX] Pleevi", favicon: "/logo-sbx.svg" };
    case "LOCAL":
      return { title: "[LOCAL] Pleevi", favicon: "/logo-local.svg" };
    default:
      return unreachable(env);
  }
})();

const amplitudeApiKey = () => {
  const env = getEnvironment();
  switch (env) {
    case "PRD":
      return import.meta.env.VITE_AMPLITUDE_API_KEY_PRODUCTION;
    case "STG":
      return import.meta.env.VITE_AMPLITUDE_API_KEY_STAGING;
    case "SBX":
      return import.meta.env.VITE_AMPLITUDE_API_KEY_SANDBOX;
    case "LOCAL":
      return undefined; // No API key for local development
    default:
      return unreachable(env);
  }
};
export const Root: React.FC = () => {
  const colorSchema = colorSchemes[getEnvironment()] ?? {
    colorPrimary: "#1e8c6f",
    colorInfo: "#1e8c6f",
  };

  const onConsent = (params: { cookie: CookieValue }) => {
    const AMPLITUDE_API_KEY = amplitudeApiKey();

    const consented_analytics = params.cookie.categories.includes("analytics");
    if (AMPLITUDE_API_KEY && consented_analytics) {
      amplitude.init(AMPLITUDE_API_KEY, {
        autocapture: {
          elementInteractions: true,
        },
        serverZone: "EU",
      });
    } else {
      // eslint-disable-next-line no-console
      console.warn("No Amplitude API key found, analytics will not be sent.");
    }
  };

  useEffect(() => {
    CookieConsent.run(getCookieConsentConfig(onConsent));
  }, []);

  return (
    <StrictMode>
      <HelmetProvider>
        <Helmet>
          <title>{helmetConfig.title}</title>
          <link rel="icon" type="image/svg+xml" href={helmetConfig.favicon} />
        </Helmet>
        <QueryClientProvider client={queryClient}>
          <ConfigProvider
            theme={{
              token: colorSchema,
              components: {
                Menu: {},
                Alert: {
                  colorInfoBg: "#e6f7ff",
                  colorInfoBorder: "#91d5ff",
                  colorInfoText: "#0050b3",
                  colorInfo: "#1890ff",
                },
              },
              algorithm: theme.defaultAlgorithm,
            }}
          >
            <NotificationProvider>
              <RouterProvider router={privateRouter()} />
            </NotificationProvider>
          </ConfigProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </StrictMode>
  );
};

createRoot(document.getElementById("root")!).render(<Root />);
